// Users component that works without logout issues
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Grid,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  AssignmentInd as AssignIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SupervisorIcon,
  LockReset as LockResetIcon,
  VpnKey as VpnKeyIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useAuthWithCustomerOverride } from '../../hooks/useAuthWithCustomerOverride';
import api from '../../services/api';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';
import { useLocation } from 'react-router-dom';
import errorLogger from '../../services/errorLogger.service';

// Create logger for this component
const logger = errorLogger.createComponentLogger('UsersAsExtensions');

function UsersAsExtensions() {
  const { member, isLoginAsCustomer } = useAuthWithCustomerOverride();
  const customerFilter = useCustomerFilter();
  const location = useLocation();
  
  // Check for login token on initialization
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = !!sessionStorage.getItem('loginAsCustomer');
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      logger.debug('Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });
  const [extensions, setExtensions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  
  // Extension assignment states
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [availableExtensions, setAvailableExtensions] = useState<any[]>([]);
  const [selectedExtensionId, setSelectedExtensionId] = useState<string>('');
  const [assignLoading, setAssignLoading] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    company_name: '',
    phone: '',
    user_type: 'member',
  });
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  
  // Helper to get customer filter params
  const getCustomerParams = () => {
    const params: any = {};
    if (isLoginAsCustomer && member?.customerID) {
      params.customerId = member.customerID;
    } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
      params.customerId = customerFilter.customerId;
    }
    return params;
  };

  const fetchExtensions = async () => {
    // SAFETY CHECK: Don't fetch if waiting for login-as data
    if (waitingForLoginAs) {
      logger.debug('Still waiting for login-as data, aborting fetch');
      return;
    }
    
    // Verify session data matches member if in login-as mode
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member?.customerID) {
          logger.error('CRITICAL: Customer ID mismatch!', {
            sessionCustomerId: parsed.customerId,
            memberCustomerId: member?.customerID
          });
          setError('Customer context mismatch - please refresh the page');
          setLoading(false);
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Get customerId from URL parameters or context
      const params = new URLSearchParams(location.search);
      const urlCustomerId = params.get('customerId');
      const contextCustomerId = customerFilter?.customerId;
      
      // Use URL parameter first, fallback to context
      const customerId = urlCustomerId || contextCustomerId;
      
      logger.debug('Fetching users with:', {
        isLoginAsCustomer,
        memberCustomerID: member?.customerID,
        urlCustomerId,
        contextCustomerId,
        customerId,
        memberType: member?.memberType,
        willFilter: customerId && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')
      });
      
      // Build API params
      const apiParams: any = {};
      // SECURITY: ALWAYS pass customerId to prevent data leakage
      if (isLoginAsCustomer && member?.customerID) {
        // When in "login as" mode, always use the overridden customer ID
        logger.debug(`LOGIN AS MODE - Using customerID: ${member.customerID}`);
        apiParams.customerId = member.customerID;
      } else if (customerId && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        apiParams.customerId = customerId;  // Changed from custid to customerId to match API
        logger.debug(`ADMIN FILTER MODE - Using customerID: ${customerId}`);
      } else if (member?.customerID) {
        // Regular customer login - MUST pass their customer ID
        apiParams.customerId = member.customerID;
        logger.debug(`CUSTOMER LOGIN - Using customerID: ${member.customerID}`);
      } else {
        logger.error('SECURITY WARNING: No customer ID available!');
      }
      
      logger.debug('API params:', apiParams);
      
      // CHANGED: Fetch users instead of extensions
      const response = await api.get('/endpoints/users', { params: apiParams });
      if (response.data.success) {
        setExtensions(response.data.data || []);
      }
    } catch (err: any) {
      logger.error('Failed to fetch:', err);
      setError(err.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle add user
  const handleAddUser = async () => {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address (e.g., user@example.com)');
      return;
    }
    
    // Validate phone number (optional but if provided, should be valid)
    if (formData.phone) {
      // Remove all non-digit characters for validation
      const digitsOnly = formData.phone.replace(/\D/g, '');
      if (digitsOnly.length < 10) {
        setError('Please enter a valid phone number (at least 10 digits)');
        return;
      }
    }
    
    // Validate required fields
    if (!formData.first_name || !formData.last_name) {
      setError('First name and last name are required');
      return;
    }
    
    try {
      setLoading(true);
      const params = getCustomerParams();
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `/endpoints/users?${queryString}` : '/endpoints/users';
      
      const response = await api.post(url, {
        ...formData,
        send_welcome_email: sendWelcomeEmail,
      });
      
      if (response.data.success) {
        setSuccess('User added successfully');
        setAddDialogOpen(false);
        fetchExtensions(); // Refresh users
        setFormData({
          email: '',
          first_name: '',
          last_name: '',
          company_name: '',
          phone: '',
          user_type: 'member',
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add user');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle edit user  
  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address (e.g., user@example.com)');
      return;
    }
    
    // Validate phone number (optional but if provided, should be valid)
    if (formData.phone) {
      // Remove all non-digit characters for validation
      const digitsOnly = formData.phone.replace(/\D/g, '');
      if (digitsOnly.length < 10) {
        setError('Please enter a valid phone number (at least 10 digits)');
        return;
      }
    }
    
    // Validate required fields
    if (!formData.first_name || !formData.last_name) {
      setError('First name and last name are required');
      return;
    }
    
    try {
      setLoading(true);
      const userId = selectedUser.id || selectedUser.mem_id;
      const params = getCustomerParams();
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `/endpoints/users/${userId}?${queryString}` : `/endpoints/users/${userId}`;
      
      const response = await api.put(url, formData);
      
      if (response.data.success) {
        setSuccess('User updated successfully');
        setEditDialogOpen(false);
        fetchExtensions(); // Refresh users
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle assign extension
  const handleAssignExtension = async (user: any) => {
    setSelectedUser(user);
    setSelectedExtensionId('');
    setAssignDialogOpen(true);

    // Fetch available (unassigned) extensions from the dedicated endpoint
    try {
      const params = getCustomerParams();
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `/endpoints/available_extensions?${queryString}` : '/endpoints/available_extensions';

      const response = await api.get(url);
      if (response.data.success) {
        // available_extensions already returns only unassigned extensions
        setAvailableExtensions(response.data.data || []);
      }
    } catch (err) {
      logger.error('Failed to fetch available extensions:', err);
      setError('Failed to load available extensions');
    }
  };

  const handleAssignSubmit = async () => {
    if (!selectedUser || !selectedExtensionId) return;
    
    setAssignLoading(true);
    try {
      const params = getCustomerParams();
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `/endpoints/extensions/${selectedExtensionId}/assign?${queryString}` : `/endpoints/extensions/${selectedExtensionId}/assign`;
      
      const userId = selectedUser.id || selectedUser.mem_id;
      const response = await api.post(url, {
        member_id: userId
      });
      
      if (response.data.success) {
        setSuccess('Extension assigned successfully');
        setAssignDialogOpen(false);
        fetchExtensions(); // Refresh the user list
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign extension');
    } finally {
      setAssignLoading(false);
    }
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    // Safety checks
    if (selectedUser.email === member?.email) {
      setError('You cannot delete your own account');
      setDeleteDialogOpen(false);
      return;
    }
    
    const userType = selectedUser.user_type || selectedUser.memberType || 'member';
    if (userType === 'master_admin') {
      setError('Master admin accounts cannot be deleted');
      setDeleteDialogOpen(false);
      return;
    }
    
    try {
      setLoading(true);
      const userId = selectedUser.id || selectedUser.mem_id;
      
      // Add customer filter params for admin override
      const params: any = {};
      if (isLoginAsCustomer && member?.customerID) {
        params.customerId = member.customerID;
      } else if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }
      
      // Pass params as query string for DELETE request
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `/endpoints/users/${userId}?${queryString}` : `/endpoints/users/${userId}`;
      const response = await api.delete(url);
      
      if (response.data.success) {
        setSuccess('User deleted successfully');
        setDeleteDialogOpen(false);
        fetchExtensions(); // Refresh users
        
        // Notify other components that a user was deleted
        // Use a more reliable event dispatching approach
        const event = new CustomEvent('userDeleted', { 
          detail: { 
            userId, 
            timestamp: Date.now(),
            extension: selectedUser.extension // Include extension if user had one
          },
          bubbles: true,
          cancelable: true
        });
        window.dispatchEvent(event);
        logger.debug(`Dispatched userDeleted event for user: ${userId}, extension: ${selectedUser.extension}`);
        
        // Also use localStorage for cross-tab communication
        localStorage.setItem('userDeleted', JSON.stringify({ 
          userId, 
          extension: selectedUser.extension,
          timestamp: Date.now() 
        }));
        // Keep it a bit longer for other components to detect
        setTimeout(() => localStorage.removeItem('userDeleted'), 500);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle toggle verification status
  const handleToggleVerification = async (user: any) => {
    try {
      setLoading(true);
      const userId = user.id || user.mem_id;
      const isVerified = user.verified || user.is_verified || user.mem_verified;
      
      const params = getCustomerParams();
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `/endpoints/users/${userId}?${queryString}` : `/endpoints/users/${userId}`;
      
      const response = await api.put(url, {
        verified: !isVerified
      });
      
      if (response.data.success) {
        setSuccess(`User ${!isVerified ? 'activated' : 'deactivated'} successfully`);
        fetchExtensions(); // Refresh users
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user status');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle password reset
  const handlePasswordReset = async (user: any) => {
    try {
      setLoading(true);
      const userId = user.id || user.mem_id;
      
      const params = getCustomerParams();
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `/endpoints/users/${userId}/reset-password?${queryString}` : `/endpoints/users/${userId}/reset-password`;
      
      const response = await api.post(url);
      
      if (response.data.success) {
        setSuccess(`Password reset email sent to ${user.email}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle resend welcome email
  const handleResendWelcome = async (user: any) => {
    try {
      setLoading(true);
      const userId = user.id || user.mem_id;
      
      const params = getCustomerParams();
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `/endpoints/users/${userId}/resend-welcome?${queryString}` : `/endpoints/users/${userId}/resend-welcome`;
      
      const response = await api.post(url);
      
      if (response.data.success) {
        setSuccess(`Welcome email sent to ${user.email}`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send welcome email');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (!member) return;
    
    // Check for login token
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoginToken = urlParams.has('lat');
    const sessionData = sessionStorage.getItem('loginAsCustomer');
    
    // If we have a login token but no session data, wait
    if (hasLoginToken && !sessionData) {
      logger.debug('Have login token but no session data - waiting...');
      return;
    }
    
    // If session data exists, verify it matches member
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.customerId && parsed.customerId !== member.customerID) {
          logger.debug('Session/member mismatch, waiting for sync...');
          return;
        }
      } catch (e) {
        logger.error('Error parsing session data:', e);
      }
    }
    
    // Add small delay to let state settle
    const timer = setTimeout(() => {
      fetchExtensions();
    }, 200);
    
    return () => clearTimeout(timer);
  }, [member?.customerID, isLoginAsCustomer]); // Re-fetch when customer ID or login-as state changes
  
  // CHANGED: Filter by user fields instead of extension fields
  const filteredExtensions = (Array.isArray(extensions) ? extensions : []).filter(ext => {
    if (!searchTerm) return true;
    return ext.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           ext.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           ext.last_name?.toLowerCase().includes(searchTerm.toLowerCase());
  });
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Users Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setFormData({
                email: '',
                first_name: '',
                last_name: '',
                company_name: '',
                phone: '',
                user_type: 'member',
              });
              setAddDialogOpen(true);
            }}
          >
            Add User
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchExtensions}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: '300px' }}
        />
      </Paper>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Extension</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredExtensions.map((user) => {
              // Check different possible field names for verification status
              const isVerified = user.verified || user.is_verified || user.mem_verified;
              const userType = user.user_type || user.memberType || 'member';
              
              return (
                <TableRow key={user.id || user.mem_id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                      {user.first_name} {user.last_name}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      {user.email}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {user.extension ? (
                      <Chip
                        icon={<PhoneIcon />}
                        label={user.extension}
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<AssignIcon />}
                        onClick={() => handleAssignExtension(user)}
                      >
                        Assign
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={userType === 'master_admin' ? 'Master Admin' : 
                             userType === 'reseller_admin' ? 'Reseller Admin' :
                             userType === 'admin' ? 'Admin' : 'Member'} 
                      size="small"
                      color={userType === 'master_admin' ? 'error' :
                             userType === 'reseller_admin' ? 'warning' :
                             userType === 'admin' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={isVerified ? 'Active' : 'Pending'} 
                      size="small"
                      color={isVerified ? 'success' : 'warning'}
                      variant="outlined"
                    />
                    </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedUser(user);
                          setFormData({
                            email: user.email,
                            first_name: user.first_name,
                            last_name: user.last_name,
                            company_name: user.company_name || '',
                            phone: user.phone || '',
                            user_type: user.user_type || 'member',
                          });
                          setEditDialogOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {/* Only show password reset for verified users */}
                    {isVerified && (
                      <Tooltip title="Reset Password">
                        <IconButton
                          size="small"
                          onClick={() => handlePasswordReset(user)}
                        >
                          <LockResetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Only show resend welcome for pending users */}
                    {!isVerified && (
                      <Tooltip title="Resend Welcome Email">
                        <IconButton
                          size="small"
                          onClick={() => handleResendWelcome(user)}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Only show delete if not current user and not a master_admin */}
                    {user.email !== member?.email && userType !== 'master_admin' && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedUser(user);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                helperText="Must be a valid email address (e.g., user@example.com)"
                error={formData.email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                helperText="Optional - Must be at least 10 digits if provided"
                error={formData.phone !== '' && formData.phone.replace(/\D/g, '').length < 10}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={formData.user_type}
                  label="User Type"
                  onChange={(e) => setFormData({ ...formData, user_type: e.target.value })}
                >
                  <MenuItem value="member">Member</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  {/* Reseller admins can also create reseller admins, but not master admins */}
                  {(member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin') && (
                    <MenuItem value="reseller_admin">Reseller Admin</MenuItem>
                  )}
                  {/* Only master admins can create other master admins */}
                  {member?.memberType === 'master_admin' && (
                    <MenuItem value="master_admin">Master Admin</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={sendWelcomeEmail}
                    onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                  />
                }
                label="Send welcome email with password setup link"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddUser} variant="contained" disabled={loading}>
            Add User
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                helperText="Must be a valid email address (e.g., user@example.com)"
                error={formData.email !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                helperText="Optional - Must be at least 10 digits if provided"
                error={formData.phone !== '' && formData.phone.replace(/\D/g, '').length < 10}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  value={formData.user_type}
                  label="User Type"
                  onChange={(e) => setFormData({ ...formData, user_type: e.target.value })}
                  disabled={selectedUser?.email === member?.email} // Disable if editing own account
                >
                  <MenuItem value="member">Member</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  {/* Reseller admins can also create reseller admins, but not master admins */}
                  {(member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin') && (
                    <MenuItem value="reseller_admin">Reseller Admin</MenuItem>
                  )}
                  {/* Only master admins can create other master admins */}
                  {member?.memberType === 'master_admin' && (
                    <MenuItem value="master_admin">Master Admin</MenuItem>
                  )}
                </Select>
              </FormControl>
              {selectedUser?.email === member?.email && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  You cannot change your own user type
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained" disabled={loading}>
            Update User
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.first_name} {selectedUser?.last_name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Assign Extension Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Extension to {selectedUser?.first_name} {selectedUser?.last_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Select an available extension to assign to this user:
            </Typography>
            
            {availableExtensions.length === 0 ? (
              <Alert severity="info">
                No unassigned extensions available. All extensions are already assigned to users.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel>Select Extension</InputLabel>
                <Select
                  value={selectedExtensionId}
                  onChange={(e) => setSelectedExtensionId(e.target.value)}
                  label="Select Extension"
                >
                  <MenuItem value="">
                    <em>Choose an extension</em>
                  </MenuItem>
                  {availableExtensions.map((ext) => (
                    <MenuItem key={ext.extension} value={ext.extension}>
                      {ext.extension} - {ext.name || 'Unnamed'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assignLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleAssignSubmit} 
            variant="contained" 
            color="success"
            disabled={!selectedExtensionId || assignLoading}
          >
            {assignLoading ? <CircularProgress size={20} /> : 'Assign Extension'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UsersAsExtensions;